// Persisted room/doorway graph for base defense, wrapping
// base-room-detection.mjs's {rooms, doorways} output with graph semantics:
// room type/isSafeRoom, doorway breach state, and BFS reachability. No
// second detection pass — the node/edge set here *is* base-room-detection's
// output, just with combat-relevant fields layered on.
//
// Pure factory functions: every mutator returns a new object without mutating
// its input, and graph membership is persisted as arrays with Sets/Maps rebuilt
// locally inside each function for lookups.
//
// Doorway "state" tracks the *defenders'* perspective, per the base-defense
// combat design's retreat rule: "intact" is a normal doorway (holding or
// unchallenged); "breached" means defenders lost that position and the room
// behind it is now enemy-exposed. That makes two distinct BFS directions
// meaningful over the same field: enemy reach is computed by walking
// BREACHED doorways (the path a horde has actually carved through, per the
// design's worked example -- reaching a dead-end safe room requires the
// front door to fall AND one full flank to also fall), while a defender's
// remaining retreat options are computed from a room's INTACT doorways.

import { EXTERIOR_ROOM_ID } from "./base-room-detection.mjs";

export { EXTERIOR_ROOM_ID };

// Every doorway starts the assault at this HP. Attackers chip it while a
// defender pokes out from behind; the door breaches at 0. Persists for the
// duration of one defense (a held-but-damaged door stays damaged) and resets
// each defense event since createTopology runs fresh each time. This is the
// natural knob a future materials/barricade feature would raise per-door.
export const DEFAULT_DOOR_HP = 5;

export function createTopology(detection, { safeRoomIds = [], doorHp = DEFAULT_DOOR_HP } = {}) {
  const safeSet = safeRoomIds instanceof Set ? safeRoomIds : new Set(safeRoomIds);
  return {
    rooms: (detection?.rooms || []).map((room) => ({
      id: room.id,
      type: null,
      isSafeRoom: safeSet.has(room.id),
    })),
    doorways: (detection?.doorways || []).map((doorway) => ({
      id: doorway.id,
      // Keep the geometric edge key: a doorway IS a grid edge, and renderers
      // need to find it on the canvas/map without re-consulting detection.
      ...(doorway.edgeKey ? { edgeKey: doorway.edgeKey } : {}),
      roomIds: [...doorway.roomIds],
      state: "intact",
      hp: doorHp,
      maxHp: doorHp,
    })),
  };
}

export function setSafeRoom(topology, roomId, isSafeRoom = true) {
  const target = topology.rooms.find((room) => room.id === roomId);
  if (!target || target.isSafeRoom === isSafeRoom) return topology;
  const rooms = topology.rooms.map((room) => (room.id === roomId ? { ...room, isSafeRoom } : room));
  return { ...topology, rooms };
}

export function breachDoorway(topology, doorwayId) {
  const target = topology.doorways.find((doorway) => doorway.id === doorwayId);
  if (!target || target.state === "breached") return topology;
  const doorways = topology.doorways.map((doorway) => (
    doorway.id === doorwayId ? { ...doorway, state: "breached", hp: 0 } : doorway
  ));
  return { ...topology, doorways };
}

// Set a doorway's remaining HP (clamped to >= 0). At 0 the door is breached
// (state flips too, so a downstream breach check needn't special-case it).
// Immutable and a no-op if the HP is unchanged, mirroring breachDoorway.
export function damageDoorway(topology, doorwayId, hpAfter) {
  const target = topology.doorways.find((doorway) => doorway.id === doorwayId);
  if (!target) return topology;
  const hp = Math.max(0, hpAfter);
  if (target.hp === hp && (hp > 0) === (target.state !== "breached")) return topology;
  const doorways = topology.doorways.map((doorway) => (
    doorway.id === doorwayId
      ? { ...doorway, hp, state: hp <= 0 ? "breached" : doorway.state }
      : doorway
  ));
  return { ...topology, doorways };
}

function adjacency(topology, stateFilter) {
  const map = new Map();
  const link = (a, b) => {
    if (!map.has(a)) map.set(a, []);
    map.get(a).push(b);
  };
  for (const doorway of topology.doorways) {
    if (!stateFilter(doorway.state)) continue;
    const [a, b] = doorway.roomIds;
    link(a, b);
    link(b, a);
  }
  return map;
}

function bfs(startId, adjacencyMap) {
  const visited = new Set([startId]);
  const queue = [startId];
  while (queue.length) {
    const current = queue.shift();
    for (const next of adjacencyMap.get(current) || []) {
      if (visited.has(next)) continue;
      visited.add(next);
      queue.push(next);
    }
  }
  return visited;
}

// Room (and possibly EXTERIOR_ROOM_ID) ids reachable from `fromRoomId` by
// walking only breached doorways -- see the module doc comment for why
// "breached" is the traversable state for this query.
export function roomsReachableFrom(topology, fromRoomId) {
  return bfs(fromRoomId, adjacency(topology, (state) => state === "breached"));
}

// BFS depth of every room from `fromRoomId`, walking every doorway
// regardless of breach state -- structural distance, not current enemy
// reachability. Used to default the safe room to the deepest room and to
// orient doorway defenders toward the exterior.
export function roomDepthsFrom(topology, fromRoomId) {
  const links = adjacency(topology, () => true);
  const depths = new Map([[fromRoomId, 0]]);
  const queue = [fromRoomId];
  while (queue.length) {
    const current = queue.shift();
    for (const next of links.get(current) || []) {
      if (depths.has(next)) continue;
      depths.set(next, depths.get(current) + 1);
      queue.push(next);
    }
  }
  return depths;
}

// True once every doorway touching `roomId` has been breached (or it has
// none at all): no intact exit remains, so a survivor sheltering there has
// nowhere left to retreat -- Death Scenario 2, trapped in an overrun
// dead-end room.
export function isRoomIsolated(topology, roomId) {
  const touching = topology.doorways.filter((doorway) => doorway.roomIds.includes(roomId));
  if (!touching.length) return true;
  return touching.every((doorway) => doorway.state === "breached");
}
